import { forwardRef } from 'react';

import type { IconProps } from 'storybookConfig/stories/molecules/Icon';
import Icon from 'storybookConfig/stories/molecules/Icon';
import type { ThemeColor } from 'storybookConfig/stories/theme';
import type { IconName } from 'storybookConfig/mixins/icons';

export enum StatusIconVariants {
  Checked = 'checked',
  Unchecked = 'unchecked',
  Error = 'error',
  Syncing = 'syncing',
  Warning = 'warning',
  PartialSuccess = 'partial_success',
}

interface StatusIconProps extends Omit<IconProps, 'name'> {
  /**
   * Controls stateful presentation
   */
  variant: `${StatusIconVariants}`;
  /**
   * Size will proportionally control the icon & text
   */
  size?: string;
}

const StatusIconBase = forwardRef<HTMLSpanElement, StatusIconProps>(
  ({ variant, size = '20px', ...rest }: StatusIconProps, ref) => {
    const names: Record<StatusIconVariants, IconName> = {
      checked: 'circle-check',
      unchecked: 'circle',
      error: 'circle-x',
      syncing: 'refresh-cw',
      warning: 'circle-alert',
      partial_success: 'circle-ellipsis',
    };

    const colors: Record<StatusIconVariants, ThemeColor> = {
      checked: 'successStrong',
      unchecked: 'bodyTextDisabled',
      error: 'errorBase',
      syncing: 'iconDefault',
      warning: 'warningStrong',
      partial_success: 'successStrong',
    };

    return <Icon name={names[variant]} ref={ref} color={colors[variant]} size={size} {...rest} />;
  }
);

/**
 * A colourised Icon for stateful status indication. The icon is a mask filled with its colour,
 * so nothing here may set a background on the pseudo element: that is the icon's own fill.
 */
const StatusIcon = StatusIconBase;

export default StatusIcon;
